import React from 'react';
import PropTypes from 'prop-types';
import { Box, TableContainer, Table, TableHead, TableBody, Typography, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { ProvisionStyledTableCell, ProvisionStyledTableRow, ProvisionActionStyledTableRow } from 'pages/myidm/style';
import deleteIcon from 'assets/images/delete_icon.svg';
import GenericTooltipText from 'pages/provisioning/day2/github-onboarding-new/components/common/GenericTooltipText';

const GenericStyledTable = ({
    title,
    groupName,
    data = [],
    columns = [],
    onDelete,
    showCount = true,
    tableSx = {},
    wrapperSx = {},
    titleSx = {},
    titleWrapperSx = {},
    noDataText = 'No data available.',
    showHeaderSection,
    withActionLabel = true,
    countLabel,
    countSuffix,
    showEdit,
    showDelete,
    onEdit,
    maxHeight = 400,
    colMaxWidth,
}) => {
    const hasAction = typeof onDelete === 'function' || typeof onEdit === 'function';

    return (
        <Box sx={{ mb: 1, mt: 2, width: '100%', ...wrapperSx }}>
            {showHeaderSection && title && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, ...titleWrapperSx }}>
                        <Typography sx={{ fontSize: '18px', fontWeight: 600, ...(titleSx || {}) }}>{title}</Typography>
                        {groupName && (groupName || '-')}
                    </Box>
                    {showCount && data?.length > 0 && (
                        <Box>
                            <b>{data.length}</b> {countLabel}(s) {countSuffix}
                        </Box>
                    )}
                </Box>
            )}

            <TableContainer sx={{ maxHeight, overflowY: 'auto', overflowX: 'auto', ...tableSx }}>
                <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                    <TableHead>
                        <ProvisionActionStyledTableRow>
                            {columns?.map((col) => (
                                <ProvisionStyledTableCell
                                    key={col.key}
                                    sx={{
                                        padding: '10px !important',
                                        backgroundColor: '#E5F5FC !important',
                                        width: hasAction ? `${85 / columns.length}%` : `${100 / columns.length}%`,
                                    }}
                                >
                                    {col.label}
                                </ProvisionStyledTableCell>
                            ))}
                            {hasAction && (
                                <ProvisionStyledTableCell
                                    sx={{ padding: '10px !important', backgroundColor: '#E5F5FC !important', textAlign: 'center', width: '15%' }}
                                >
                                    {withActionLabel && 'Action'}&nbsp;
                                </ProvisionStyledTableCell>
                            )}
                        </ProvisionActionStyledTableRow>
                    </TableHead>

                    <TableBody>
                        {data?.length > 0 ? (
                            data.map((row, rowIndex) => (
                                <ProvisionStyledTableRow key={row?.id || rowIndex} className="animate-opacity">
                                    {columns?.map((col) => (
                                        <ProvisionStyledTableCell
                                            key={col?.key}
                                            sx={{
                                                cursor: 'default',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <GenericTooltipText
                                                isTypography
                                                showTooltip
                                                tooltipContent={row[col?.key]}
                                                text={row[col?.key] || '-'}
                                                ellipsis
                                                sx={{
                                                    maxWidth: colMaxWidth || '100%',
                                                }}
                                            />
                                        </ProvisionStyledTableCell>
                                    ))}
                                    {hasAction && (
                                        <ProvisionStyledTableCell align="end" sx={{ textAlign: 'center', width: '15%' }}>
                                            {showEdit && (
                                                <IconButton aria-label="edit" title="Edit" onClick={() => onEdit(rowIndex)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            {showDelete && (
                                                <IconButton aria-label="delete" title="Delete" size="small" onClick={() => onDelete(groupName, row)}>
                                                    <img src={deleteIcon} alt="deleteIcon" style={{ width: 16, height: 16 }} />
                                                </IconButton>
                                            )}
                                        </ProvisionStyledTableCell>
                                    )}
                                </ProvisionStyledTableRow>
                            ))
                        ) : (
                            <ProvisionStyledTableRow>
                                <ProvisionStyledTableCell align="center" colSpan={columns.length + (hasAction ? 1 : 0)}>
                                    {noDataText}
                                </ProvisionStyledTableCell>
                            </ProvisionStyledTableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

GenericStyledTable.propTypes = {
    title: PropTypes.string,
    showHeaderSection: PropTypes.bool,
    withActionLabel: PropTypes.bool,
    groupName: PropTypes.string,
    data: PropTypes.array.isRequired,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        })
    ).isRequired,
    onDelete: PropTypes.func,
    onEdit: PropTypes.func,
    showCount: PropTypes.bool,
    showDelete: PropTypes.bool,
    showEdit: PropTypes.bool,
    tableSx: PropTypes.object,
    titleSx: PropTypes.object,
    titleWrapperSx: PropTypes.object,
    wrapperSx: PropTypes.object,
    noDataText: PropTypes.string,
    countLabel: PropTypes.string,
    countSuffix: PropTypes.string,
    maxHeight: PropTypes.number,
    colMaxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

GenericStyledTable.defaultProps = {
    title: '',
    showHeaderSection: false,
    withActionLabel: true,
    groupName: '',
    onDelete: undefined,
    onEdit: undefined,
    showCount: true,
    showEdit: false,
    showDelete: true,
    tableSx: {},
    titleSx: {},
    titleWrapperSx: {},
    wrapperSx: {},
    noDataText: 'No data available.',
    countLabel: 'Member',
    countSuffix: 'selected',
    maxHeight: 400,
    colMaxWidth: '100%',
};

export default GenericStyledTable;
